import { useEffect, useRef, useState } from "react";
import { GoInfo } from "react-icons/go";
import Modal from "./Modal";
import FaqComponent from "./FaqComponent";
import { calculateQuote } from "../Api/CalculateQuote";
import seedrandom from "seedrandom";
import { EVENTS, trackEvent, isExistingCustomerAnchoringEnabled } from "../utils/statsig";
import { getPromo, applyPromo } from "../_lib/promos";
import brandConfig from "../config/brand";
import { getEmailSuggestion } from "../utils/emailSuggestion";
import EmailSuggestion from "./EmailSuggestion";

const featureData = [
  {
    icon: "/images/technician-icon.svg",
    title: "Certified Technicians (CPO)",
    description:
      "All of techs are Certified Pool Operators (CPOs) by the Pool & Hot Tub Alliance (PHTA).",
  },
  {
    icon: "/images/guarantee-icon.svg",
    title: "World Class Support",
    description:
      "Our support team is available 7 days a week to help with any questions or concerns.",
  },
  {
    icon: "/images/cancel-anytime.svg",
    title: "Cancel Anytime",
    description:
      "We don't lock you into any contracts and you can cancel at any time.",
  },
  {
    icon: "/images/insurance-icon.svg",
    title: "Bonded & Insured",
    description:
      "We carry a multimillion dollar liability insurance policy so you're fully covered.",
  },
];

const FeatureItem = ({ icon, title, children }) => (
  <div className="feature-item">
    <img src={icon} alt={title} />
    <div className="feature-text-content">
      <p className="feature-title">{title}</p>
      <p className="feature-description">{children}</p>
    </div>
  </div>
);

const generateNeighbourCount = (zipCode, min, max) => {
  const rng = seedrandom(zipCode);
  return Math.floor(rng() * (max - min + 1)) + min;
};

const QuoteReview = ({ onNext, currentData, zipCode, poolURL }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    amount: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteFailed, setQuoteFailed] = useState(false);
  // Set when the address is one we already service but the engine declined
  // to auto-price it. Distinct from quoteFailed: nothing broke, and a retry
  // would return the same answer.
  const [needsReview, setNeedsReview] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const emailTrackedRef = useRef(false);
  // First claim press on a likely email typo holds and surfaces the "Did you
  // mean" suggestion instead of submitting. Accepting it or pressing claim
  // again proceeds.
  const typoAckRef = useRef(false);

  // Promo flag: set when the visitor arrived from a Meta retargeting URL
  // (utm_campaign starts with "retargeting") or directly via ?promo=...
  // Captured into localStorage in App.js. Only first-month-monthly promos
  // surface in this slideshow flow; invoice-total promos (e.g.
  // service200comp) are set manually on the Sell deal and show on the
  // dealId-based QuotePage instead.
  const promoFlag = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('promo')
    : null;
  const promoCfg = getPromo(promoFlag);
  const hasPromo = !!promoCfg && promoCfg.appliesTo === 'monthly_first_month';

  // Read once per mount; the helper itself caches the decision for the
  // session so the displayed price and the submitted price cannot disagree.
  const anchoringEnabled = isExistingCustomerAnchoringEnabled();

  const fetchCalculateQuote = async () => {
    setLoadingStates((prev) => ({ ...prev, 'monthly-pool-service': true }));
    setQuoteFailed(false);
    setNeedsReview(false);

    const quoteData = {
      serviceId: 'monthly-pool-service',
      brand: brandConfig.pricingBrand,
      quantity: 1,
      zipCode: zipCode,
    }

    // Existing-customer anchoring, behind the Statsig gate. The engine matches
    // the service address against the billed-address index and requires the
    // phone or surname to corroborate before it will quote someone their own
    // rate instead of the regional standard. Sending these three fields is the
    // whole client-side change: resolution is a dict lookup inside the call we
    // already make, so it adds no round trip and no measurable latency.
    //
    // Withholding them is exactly the pre-feature behavior, which is what makes
    // this a clean 50/50: control cannot be anchored because the engine never
    // learns who is asking. The same decision rides along on the submission so
    // cabana-lead-processor recomputes the price the way this screen showed it.
    if (anchoringEnabled) {
      quoteData.address = currentData?.address;
      quoteData.personName = currentData?.personName;
      quoteData.phone = currentData?.phone;
    }

    const modifiers = []
    if (currentData.poolOption === 'pool_spa') {
      modifiers.push('spa');
    }
    if (currentData.poolOption === 'pool_hot_tub') {
      // Unattached above-ground spa (hot tub) next to the pool: $50 add-on,
      // distinct from the attached-spa modifier.
      modifiers.push('above-ground-spa');
    }
    if (currentData.poolOption1 && Array.isArray(currentData.poolOption1) && currentData.poolOption1.includes('trees_over_pool')) {
      modifiers.push('trees');
    }
    if (currentData.poolSize === 'large') {
      modifiers.push('large-pool');
    }
    quoteData.modifiers = modifiers;

    const res = await calculateQuote(quoteData)
    if (res && res.totalPrice) {
      setQuote(res);
      setNeedsReview(false);
    } else if (res && res.requiresManualQuote === true) {
      // Not a failure. The engine recognised this address as one we already
      // service but could not derive a rate it is willing to quote
      // automatically (a different party at a known address, a spa-only
      // customer asking for pool service, an implausible basis). Showing the
      // standard regional price here is exactly the mispricing this feature
      // exists to prevent, so the customer gets a review path instead of a
      // number, and a retry would only produce the same answer.
      setNeedsReview(true);
      setQuoteFailed(false);
    } else {
      setQuoteFailed(true);
      setNeedsReview(false);
    }
    setLoadingStates((prev) => ({ ...prev, 'monthly-pool-service': false }));
  }

  useEffect(() => {
    const handleResize = () => setIsOpen(window.innerWidth > 1024);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (currentData.email) {
      handleChange({
        target: { name: 'email', value: currentData.email }
      })
    }
    fetchCalculateQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set the quote amount in formData when quote is loaded
  useEffect(() => {
    if (quote && quote.totalPrice) {
      setFormData((prev) => ({
        ...prev,
        amount: quote.totalPrice
      }));
    }
  }, [quote]);

  const openModal = (content) => {
    setModalContent(content);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  const isEmailValid = formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          error = "Please enter a valid email address";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = (formData) => {
    const errors = {};
    const fieldsToValidate = ['email'];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    const error = validateField(name, finalValue);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    if (name === 'email') { setEmailSuggestion(null); typoAckRef.current = false; }

    // Track first valid email entry
    if (name === 'email' && !error && !emailTrackedRef.current) {
      emailTrackedRef.current = true;
      trackEvent(EVENTS.EMAIL_ENTERED, { zip_code: zipCode });
    }
  };

  const handleEmailBlur = (e) => {
    setEmailSuggestion(getEmailSuggestion(e.target.value));
  };

  const acceptEmailSuggestion = (suggestion) => {
    setFormData((prev) => ({ ...prev, email: suggestion }));
    setFormErrors((prev) => ({ ...prev, email: validateField("email", suggestion) }));
    setEmailSuggestion(null);
    typoAckRef.current = false;
  };

  const handleSubmit = async () => {
    const emailTypoSuggestion = getEmailSuggestion(formData.email);
    if (emailTypoSuggestion && !typoAckRef.current) {
      setEmailSuggestion(emailTypoSuggestion);
      typoAckRef.current = true;
      return;
    }
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    if (isProcessing) return;
    setIsProcessing(true);

    try {
      await onNext(true, true, {
        email: formData.email,
        amount: formData.amount,
        // Carry the gate decision so the backend recompute matches the number
        // this screen displayed, including the no-number manual-review case.
        ec_anchoring: anchoringEnabled,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const neighbourCount = generateNeighbourCount(zipCode, 10, 60);
  const poolImageSrc = poolURL && poolURL !== "/images/thank-you.svg" ? poolURL : "/images/quote-payment.svg";

  return (
    <>
      <div className="payment-container-wrapper">
        <div className="quote-container">
          <div className="quote-inner-container">
            <div className="quote-header">
              <h4>Your Personalized Weekly Pool Service Quote at {currentData?.address}</h4>
            </div>
            <div className="pool-service-detail">
              <img src={poolImageSrc} alt="Your pool" />
              <div className="pool-service-info">
                <div className="pool-service-header">
                  <h6>Weekly Pool Service</h6>
                  <GoInfo color="#00000080" size={14} />
                </div>
                {loadingStates["monthly-pool-service"] ? (
                  <div className="pool-service-price">
                    <h1><div className="loader small"></div></h1>
                    <span>per month</span>
                  </div>
                ) : quote?.totalPrice ? (
                  <>
                    <div className="pool-service-price">
                      <h1>${quote.totalPrice}</h1>
                      <span>per month</span>
                    </div>
                    {hasPromo && (
                      <div className="promo-applied-callout">
                        <span className="promo-tag">{promoCfg.label}, applied</span>
                        <span className="promo-first-month-line">
                          First month: <strong>${applyPromo(quote.totalPrice, promoFlag)}</strong>
                          <span className="promo-strike">${quote.totalPrice}</span>
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="pool-service-price-unavailable">
                    {needsReview && (
                      <p className="quote-review-note">
                        We already service this address, so we want to get your
                        pricing exactly right. Send us your email and someone on
                        our team will confirm your quote.
                      </p>
                    )}
                    {quoteFailed && (
                      <button type="button" className="retry-quote-btn" onClick={fetchCalculateQuote}>
                        Try again
                      </button>
                    )}
                  </div>
                )}
                <p>Our CPO-certified technicians test chemicals, brush walls, empty baskets, check equipment, and skim weekly.</p>
              </div>
            </div>

            <div className="perk-section">
              <div className="features-wrapper">
                {isOpen && (
                  <div className="features-container">
                    <div className="features-grid">
                      {featureData.map((item, index) => (
                        <FeatureItem key={index} icon={item.icon} title={item.title}>
                          {item.description}
                        </FeatureItem>
                      ))}
                    </div>
                  </div>
                )}

                {isOpen && (
                  <div className="faq-section">
                    <p>Got questions? <a href="#faq" onClick={(e) => {
                      e.preventDefault();
                      trackEvent(EVENTS.FAQ_OPENED, { zip_code: zipCode });
                      openModal(<FaqComponent onClose={closeModal} />);
                    }}>Read our FAQs</a></p>
                  </div>
                )}

                <button onClick={() => {
                  trackEvent(EVENTS.DETAILS_TOGGLED, { action: isOpen ? 'collapse' : 'expand', zip_code: zipCode });
                  setIsOpen(!isOpen);
                }} className="toggle-button">
                  {isOpen ? 'Show Less Details' : 'Details'}
                  <span className={`arrow ${isOpen ? 'up' : 'down'}`}></span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="claim-quote-section">
          <label htmlFor="howWeReachYou" className="claim-quote-label">
            {needsReview
              ? "Where can we send your confirmed quote?"
              : quoteFailed
                ? "Something went wrong and we'll need to manually review."
                : "Where can we send your quote?"}
          </label>
          <div className="claim-quote-row">
            <input
              className={`form-control input-field${formErrors.email ? " invalid-field" : ""}`}
              placeholder="Email"
              name="email"
              id="howWeReachYou"
              type="email"
              pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
              title="Email should be in the format: example@example.com."
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
            />
            <button
              className="btn btn-primary book-btn load-button"
              onClick={handleSubmit}
              disabled={!isEmailValid}
            >{(quoteFailed || needsReview) ? "REQUEST MY QUOTE" : "CLAIM MY QUOTE"}</button>
          </div>
          {formErrors.email && <div className="field-error-message">{formErrors.email}</div>}
          <EmailSuggestion
            suggestion={emailSuggestion}
            onAccept={acceptEmailSuggestion}
          />
          <p className="social-proof-text">
            Join {neighbourCount} neighbors in {zipCode} who trust Cabana with their pool.
          </p>
        </div>
      </div>
      <input type="hidden" id="progress_slide_1" data-width="95%" />
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {modalContent}
      </Modal>
    </>
  );
};

export default QuoteReview
